import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const LanguageContext = createContext();

// Only an explicit choice (or the account's language, see Session.js) is
// stored, so visitors without one keep following the timezone default.
const STORAGE_KEY = 'gm-language';
const SUPPORTED = ['en', 'tr'];

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

/** Turkish for visitors in Turkey's timezone, English for everyone else. */
function defaultLanguage() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (['Europe/Istanbul', 'Asia/Istanbul', 'Turkey'].includes(tz)) return 'tr';
    if (tz) return 'en';
  } catch (e) {
    // Intl unavailable — fall back to the browser language below.
  }
  return typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('tr') ? 'tr' : 'en';
}

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (SUPPORTED.includes(saved)) return saved;
    } catch (e) {
      // storage unavailable (private mode)
    }
    return defaultLanguage();
  });

  const setLanguage = useCallback((lang) => {
    if (!SUPPORTED.includes(lang)) return;
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value = {
    language,
    setLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
